import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "@tanstack/react-query";
import { AUDIT_CATEGORY_ACTIONS } from "@nexus/contracts";
import { Button, cn, TooltipProvider } from "@nexus/ui";
import { FileText, Mail, Shield, X } from "lucide-react";
import { globalAuditLogInfiniteQueryOptions } from "@/features/identity-hub/lib/queries/audit-log.queries";
import type { InviteListItem } from "@/features/identity-hub/api/identity-hub.api";
import { relativeTime } from "@/features/identity-hub/utils/time";
import { UserPhotoMark } from "./UserPhotoMark";
import { UserStatusBadge } from "./shared/UserStatusBadge";
import { DrawerStat } from "./shared/drawer/DrawerStat";
import { DrawerTabStrip, type DrawerTabDef } from "./shared/drawer/DrawerTabStrip";
import { IamOverlayDrawer } from "./shared/drawer/IamOverlayDrawer";
import { OrgLogoMark } from "./OrgLogoMark";
import { usePermissions, useIsSuperTenant } from "@/features/auth/hooks/usePermissions";

export type InviteDrawerTab = "overview" | "audit";

export interface InviteDrawerProps {
  invite: InviteListItem | null;
  open?: boolean;
  initialTab?: InviteDrawerTab;
  onClose: () => void;
  onCopyLink?: (invite: InviteListItem) => void;
  onResend?: (invite: InviteListItem) => void;
  onRegenerate?: (invite: InviteListItem) => void;
  onRevoke?: (invite: InviteListItem) => void;
  busy?: boolean;
}

function inviteStatusToUserStatus(status: InviteListItem["status"]): "ACTIVE" | "PENDING" | "INACTIVE" {
  if (status === "accepted") return "ACTIVE";
  if (status === "pending") return "PENDING";
  return "INACTIVE";
}

function InviteAuditList({ email, hasPermission }: { email: string; hasPermission: boolean }) {
  const { t } = useTranslation();
  const needle = email.trim().toLowerCase();
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    ...globalAuditLogInfiniteQueryOptions({
      actionIn: AUDIT_CATEGORY_ACTIONS.identity,
      pageSize: 50,
    }),
    enabled: hasPermission && needle.length > 0,
  });

  const items = useMemo(() => {
    const raw = data?.pages.flatMap((page) => page.items) ?? [];
    return raw.filter((item) => {
      const hay = [
        item.targetEmail ?? "",
        item.targetName ?? "",
        item.actorEmail ?? "",
        typeof item.metadata.email === "string" ? item.metadata.email : "",
      ].join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [data, needle]);

  if (!hasPermission) {
    return (
      <p className="px-1 py-6 text-center text-[12px] text-muted-foreground">
        {t("iam.auditLog.timeline.noPermission", "You don’t have permission to view audit logs.")}
      </p>
    );
  }
  if (isLoading) {
    return <p className="px-1 py-6 text-center text-[12px] text-muted-foreground">{t("iam.auditLog.loading", "Loading…")}</p>;
  }
  if (items.length === 0) {
    return (
      <p className="px-1 py-6 text-center text-[12px] text-muted-foreground">
        {t("iam.inviteDrawer.auditEmpty", "No audit events for this invite yet.")}
      </p>
    );
  }

  return (
    <ul className="flex flex-col">
      {items.map((item, i) => (
        <li
          key={item.id}
          className={cn("py-2.5", i < items.length - 1 && "border-b border-border/50")}
        >
          <p className="text-[12.5px] leading-snug">
            <span className="font-medium text-foreground">
              {item.actorDisplayName || item.actorEmail || t("iam.auditLog.fallback.unknownActor")}
            </span>
            {" "}
            <span className="text-muted-foreground">
              {t(`iam.auditLog.timeline.verb.${item.action}`, { defaultValue: item.action.replace(/_/g, " ") })}
            </span>
          </p>
          <p className="text-[11px] text-muted-foreground">{relativeTime(item.createdAt)}</p>
        </li>
      ))}
      {hasNextPage && (
        <li className="pt-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {t("iam.auditLog.timeline.loadMore", "Load more")}
          </Button>
        </li>
      )}
    </ul>
  );
}

export function InviteDrawer({
  invite,
  open = true,
  initialTab = "overview",
  onClose,
  onCopyLink,
  onResend,
  onRegenerate,
  onRevoke,
  busy = false,
}: InviteDrawerProps) {
  const { t } = useTranslation();
  const perms = usePermissions();
  const isSuperTenant = useIsSuperTenant();
  const canViewAuditLog = isSuperTenant || perms.IDENTITY_HUB_VIEW_AUDIT_LOG.isOn;
  const [tab, setTab] = useState<InviteDrawerTab>(initialTab);

  useEffect(() => {
    setTab(initialTab);
  }, [invite?.id, initialTab]);

  const accessPairs = invite?.accesses?.length
    ? invite.accesses
    : invite?.tenantName || invite?.accessName
      ? [{
          accessId: invite.accessId ?? "",
          tenantId: invite.tenantId ?? "",
          tenantName: invite.tenantName,
          roleName: invite.accessName,
        }]
      : [];

  const tabs: Array<DrawerTabDef<InviteDrawerTab>> = [
    { id: "overview", label: t("iam.inviteDrawer.tabs.overview", "Overview"), Icon: Mail },
    {
      id: "audit",
      label: t("iam.inviteDrawer.tabs.audit", "Audit"),
      Icon: FileText,
    },
  ];

  const handleOpenChange = (next: boolean) => {
    if (!next) onClose();
  };

  return (
    <IamOverlayDrawer
      open={open && !!invite}
      onOpenChange={handleOpenChange}
      title={invite?.email ?? t("iam.dashboard.tabs.invites")}
    >
      {invite ? (
        <TooltipProvider delayDuration={200}>
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-start justify-between gap-2.5 border-b border-border px-5 py-4">
            <div className="flex min-w-0 items-center gap-3">
              <UserPhotoMark name={invite.email} size="lg" />
              <div className="min-w-0">
                <h3 className="truncate text-[15px] font-semibold leading-tight">{invite.email}</h3>
                <p className="truncate text-[12px] text-muted-foreground">
                  {accessPairs.map((p) => p.tenantName ?? p.tenantId).filter(Boolean).join(", ") || "—"}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={t("iam.inviteDrawer.closePanel", "Close panel")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-3 border-b border-border">
            <DrawerStat label={t("iam.inviteDrawer.stats.status")} tooltip={t("iam.invites.columns.status")} valueClassName="">
              <UserStatusBadge status={inviteStatusToUserStatus(invite.status)} variant="plain" />
            </DrawerStat>
            <DrawerStat label={t("iam.inviteDrawer.stats.expires")} tooltip={t("iam.invites.columns.expires")}>
              {relativeTime(invite.expiresAt)}
            </DrawerStat>
            <DrawerStat label={t("iam.inviteDrawer.stats.orgs")} tooltip={t("iam.invites.columns.org")}>
              {accessPairs.length}
            </DrawerStat>
          </div>

          <DrawerTabStrip tabs={tabs} activeTab={tab} onTabChange={setTab} />

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
            {tab === "overview" && (
              <>
                <div className="flex flex-col gap-1">
                  <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t("iam.inviteDrawer.sentBy")}
                  </span>
                  <p className="text-sm">
                    {invite.createdByName ?? invite.createdByEmail ?? t("iam.invites.systemActor", "System")}
                    {invite.createdByEmail && invite.createdByName ? ` · ${invite.createdByEmail}` : ""}
                  </p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t("iam.inviteDrawer.roles")}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {accessPairs.length === 0 ? (
                      <span className="text-sm text-muted-foreground">—</span>
                    ) : (
                      accessPairs.map((pair) => (
                        <span
                          key={pair.accessId || `${pair.tenantId}-${pair.roleName}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-[11px]"
                        >
                          <OrgLogoMark name={pair.tenantName ?? pair.tenantId} size={14} />
                          <Shield className="h-3 w-3" />
                          {pair.roleName ?? "—"}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
            {tab === "audit" && (
              <InviteAuditList email={invite.email} hasPermission={canViewAuditLog} />
            )}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-4 py-3">
            {invite.status !== "accepted" && onCopyLink && (
              <Button size="sm" variant="outline" iconStart="copy" disabled={busy} onClick={() => onCopyLink(invite)}>
                {t("iam.inviteDrawer.copyLink")}
              </Button>
            )}
            {invite.status === "pending" && onResend && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => onResend(invite)}>
                {t("iam.inviteDrawer.resend")}
              </Button>
            )}
            {invite.status !== "accepted" && onRegenerate && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => onRegenerate(invite)}>
                {t("iam.inviteDrawer.regenerate")}
              </Button>
            )}
            {invite.status === "pending" && onRevoke && (
              <Button size="sm" variant="destructive" disabled={busy} onClick={() => onRevoke(invite)}>
                {t("iam.inviteDrawer.revoke")}
              </Button>
            )}
          </div>
        </div>
        </TooltipProvider>
      ) : null}
    </IamOverlayDrawer>
  );
}
